import { useState, useCallback, useRef } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { acquireApiToken } from '@/auth/acquireApiToken';
import { getAdminApiUrl } from "../../shared";
import { NotificationAudience, RecurrenceDay } from "./useDeals";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CreateDealRequest {
  environment: "dev" | "staging" | "prod";
  businessId: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  notificationAudience: NotificationAudience[];
  isRecurring?: boolean;
  recurrenceDays?: RecurrenceDay[];
  recurrenceEndDate?: string | null;
}

export interface CreateDealResponse {
  id: string;
  businessId: string;
  title: string;
  description: string;
  dealType: string;
  startTime: string;
  endTime: string;
  notificationAudience: NotificationAudience[];
  status: "scheduled" | "active" | "expired" | "deleted";
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  recurrenceDays: RecurrenceDay[] | null;
  recurrenceEndDate: string | null;
  parentDealId: string | null;
}

export interface UpdateDealRequest {
  environment: "dev" | "staging" | "prod";
  id: string;
  title?: string;
  description?: string;
  startTime?: string;
  endTime?: string;
  notificationAudience?: NotificationAudience[];
  isRecurring?: boolean;
  recurrenceDays?: RecurrenceDay[] | null;
  recurrenceEndDate?: string | null;
}

export interface UpdateDealResponse extends CreateDealResponse {
  updatedAt: string;
}

export interface DeleteDealRequest {
  environment: "dev" | "staging" | "prod";
  id: string;
}

export interface DeleteDealResponse {
  deleted: boolean;
  dealId: string;
  alreadyDeleted?: boolean;
}

// ── useCreateDeal ──────────────────────────────────────────────────────────────

export function useCreateDeal() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (input: CreateDealRequest): Promise<CreateDealResponse | null> => {
      const apiUrl = getAdminApiUrl(input.environment);
      if (!apiUrl) {
        setError("API URL is not configured for this environment");
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const { environment, ...payload } = input;

        const response = await fetch(`${apiUrl}/admin/deals`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (!response.ok) {
          setError(data.error || `HTTP ${response.status}`);
          return null;
        }

        return (data.deal ?? data) as CreateDealResponse;
      } catch (err: any) {
        setError(err?.message ?? "Unknown error");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, instance],
  );

  return { loading, error, create };
}

// ── useUpdateDeal ──────────────────────────────────────────────────────────────

export function useUpdateDeal() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(
    async (input: UpdateDealRequest): Promise<UpdateDealResponse | null> => {
      const apiUrl = getAdminApiUrl(input.environment);
      if (!apiUrl) {
        setError("API URL is not configured for this environment");
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const { environment, id, ...payload } = input;

        const response = await fetch(`${apiUrl}/admin/deals/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (!response.ok) {
          setError(data.error || `HTTP ${response.status}`);
          return null;
        }

        return (data.deal ?? data) as UpdateDealResponse;
      } catch (err: any) {
        setError(err?.message ?? "Unknown error");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, instance],
  );

  return { loading, error, update };
}

// ── useDeleteDeal ──────────────────────────────────────────────────────────────

export function useDeleteDeal() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = useCallback(
    async (input: DeleteDealRequest): Promise<DeleteDealResponse | null> => {
      const apiUrl = getAdminApiUrl(input.environment);
      if (!apiUrl) {
        setError("API URL is not configured for this environment");
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const response = await fetch(`${apiUrl}/admin/deals/${input.id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const data = await response.json();
        if (!response.ok) {
          setError(data.error || `HTTP ${response.status}`);
          return null;
        }

        return data as DeleteDealResponse;
      } catch (err: any) {
        setError(err?.message ?? "Unknown error");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, instance],
  );

  return { loading, error, remove };
}
